// React
import { useEffect, useRef, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { router } from './Router';
import PWAPrompt from 'react-ios-pwa-prompt';

// Futurelab
import Loader from './components/common/Loader';
import { useAppSelector } from './store/hooks';
import { SocketProvider } from './hooks/useSocket';
import { TooltipProvider } from './components/ui/tooltip';
import { useOnlineStatus } from './hooks/useOnlineStatus';

// CSS
import './assets/tailwind.css';
import './assets/main.css';

import { identifyMixpanelUser, initializeMixpanel } from './services/mixpanel';

function App() {
	initializeMixpanel();
	const isLoading = useAppSelector((state) => state.showLoader);
	const [showOnlineMessage, setShowOnlineMessage] = useState<boolean>(false);
	const loggedInUserDetails = useAppSelector((state) => state.loggedInUserDetails);
	const isOnline = useOnlineStatus();
	const wasOffline = useRef(!isOnline);

	useEffect(() => {
		if ('id' in loggedInUserDetails && loggedInUserDetails.id) {
			identifyMixpanelUser(`${loggedInUserDetails.id}`);
		}
	}, [loggedInUserDetails]);

	useEffect(() => {
		if (!isOnline) {
			wasOffline.current = true;
			setShowOnlineMessage(false);
			return;
		}
		if (!wasOffline.current) return;

		wasOffline.current = false;
		setShowOnlineMessage(true);
		const timeout = window.setTimeout(() => setShowOnlineMessage(false), 3000);
		return () => window.clearTimeout(timeout);
	}, [isOnline]);

	return (
		<TooltipProvider>
			<SocketProvider>
				<div className="min-h-screen overflow-x-hidden bg-background text-foreground">
					{isLoading && <Loader />}
					<RouterProvider router={router} />
					{showOnlineMessage && (
						<div
							aria-live="polite"
							className="fixed inset-x-4 top-4 z-50 break-words rounded-md bg-primary px-4 py-3 text-center text-sm font-medium text-primary-foreground shadow-lg sm:inset-x-auto sm:left-1/2 sm:w-fit sm:max-w-[calc(100%-2rem)] sm:-translate-x-1/2"
						>
							Internet connection back to normal
						</div>
					)}
					{!isOnline && (
						<div
							role="status"
							className="fixed inset-x-4 top-4 z-50 break-words rounded-md bg-red-600 px-4 py-3 text-center text-sm font-medium text-white shadow-lg sm:inset-x-auto sm:left-1/2 sm:w-fit sm:max-w-[calc(100%-2rem)] sm:-translate-x-1/2"
						>
							You seem to have lost internet connection
						</div>
					)}
				</div>
				<PWAPrompt />
			</SocketProvider>
		</TooltipProvider>
	);
}

export default App;
